/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { PAYMENT_STATUS } from "pages/AccountSettlementHistoryOrderId/constants/PaymentStatus";
import {
  hideOnDesktopView,
  infoWrapperStyle,
  infoTitleTextStyle,
  infoDetailTextStyle,
} from "pages/AccountSettlementHistoryOrderId/styles/commonStyles";
import { getDeliveryCarrierName } from "pages/AccountSettlementHistoryOrderId/utils/getDeliveryCarrierName";
import { getPaymentStatusLabel } from "pages/AccountSettlementHistoryOrderId/utils/getPaymentStatusLabel";
import { Fragment } from "react";
import Skeleton from "react-loading-skeleton";
import { StatusLabel } from "ui/StatusLabel";
import { SHIPMENT_STATUS } from "ui/StatusLabel/constants/shipmentStatus";

import { Copy } from "../icons/Copy";
import { ReceiptOrPaymentAlert } from "./components/ReceiptOrPaymentAlert";
import { TrackingButton } from "./components/TrackingButton";
import {
  alertTextStyle,
  copyButtonWrapperStyle,
  labelWithValueStyle,
  orderInfoWrapperStyle,
  orderNumberRowStyle,
  purchaseDateStyle,
  labelWrapperStyle,
  rootStyle,
  topContentStyle,
  trackingInfoWrapperStyle,
} from "./styles";

import type { DeliveryData } from "pages/AccountSettlementHistoryOrderId/types/DeliveryData";
import type { FulfillmentData } from "pages/AccountSettlementHistoryOrderId/types/FulfillmentData";
import type { OrderData } from "pages/AccountSettlementHistoryOrderId/types/OrderData";
import type { FC } from "react";

type Props = {
  deliveryData?: DeliveryData;
  fulfillmentData?: FulfillmentData;
  isLoadingAddress: boolean;
  isLoadingFulfillment: boolean;
  /** PayPal決済が審査保留中かどうか。支払案内の文言を切り替える */
  isPayPalPending: boolean;
  isPurchased: boolean;
  /** 領収書を出せるかどうか。falseのとき領収書ボタンを出さない */
  isReceiptAvailable: boolean;
  onContinuePayment: () => void;
  onCopyOrderId: (orderId: string) => void;
  onShowReceipt: () => void;
  orderData: OrderData;
  /** 未払いの注文に支払案内を出すかどうか */
  showPaymentAlert: boolean;
};

const formatPurchaseDate = (purchasedAt: number, language: string): string => {
  const date = new Date(purchasedAt * 1000);
  if (isNaN(date.getTime())) return "";
  const formatPattern = language === "ja" ? "yyyy年MM月dd日" : "yyyy/MM/dd";
  return format(date, formatPattern);
};

export const OrderInfo: FC<Props> = (props) => {
  const {
    deliveryData,
    fulfillmentData,
    isLoadingAddress,
    isLoadingFulfillment,
    isPayPalPending,
    isPurchased,
    isReceiptAvailable,
    onContinuePayment,
    onCopyOrderId,
    onShowReceipt,
    orderData,
    showPaymentAlert,
  } = props;
  const {
    orderId,
    purchasedAt,
    purchasedCount,
    purchasedType,
    paymentStatus,
    totalPrice,
  } = orderData;

  const { recipientName, postalCode, mainAddress, subAddress } =
    deliveryData ?? {};

  const formattedPrice = totalPrice.toLocaleString("ja-JP");

  const { t, i18n } = useI18n();

  const formattedDate = formatPurchaseDate(purchasedAt, i18n.language);

  const shouldShowShippingInfo =
    !isLoadingFulfillment && fulfillmentData?.shippingStatus === "shipped";

  return (
    <div css={rootStyle}>
      <div css={topContentStyle}>
        <div css={orderInfoWrapperStyle}>
          <h4 css={infoTitleTextStyle}>{t("settlementHistory.orderDate")}</h4>
          <time
            css={purchaseDateStyle}
            dateTime={new Date(purchasedAt * 1000).toISOString()}
          >
            <span>{formattedDate}</span>
          </time>
        </div>
        <div css={labelWrapperStyle}>
          {isLoadingFulfillment && <Skeleton width={72} height={24} />}
          {shouldShowShippingInfo && (
            <StatusLabel status={SHIPMENT_STATUS.SHIPPED} />
          )}
        </div>
      </div>
      <div css={orderInfoWrapperStyle}>
        <h4 css={infoTitleTextStyle}>{t("settlementHistory.orderNumber")}</h4>
        <div css={orderNumberRowStyle}>
          <p css={infoDetailTextStyle}>
            <span>{orderId}</span>
          </p>
          <button
            css={copyButtonWrapperStyle}
            onClick={() => onCopyOrderId(orderId)}
            aria-label={t("settlementHistory.copyOrderId")}
          >
            <Copy />
          </button>
        </div>
      </div>
      {isLoadingFulfillment ? (
        <div css={trackingInfoWrapperStyle}>
          <p css={infoDetailTextStyle}>
            <Skeleton width={150} />
          </p>
          <p css={infoDetailTextStyle}>
            <Skeleton width={200} />
          </p>
          <Skeleton width={140} height={30} />
        </div>
      ) : (
        shouldShowShippingInfo &&
        fulfillmentData.trackingCode && (
          <div css={trackingInfoWrapperStyle}>
            {fulfillmentData.carrier && (
              <p css={infoDetailTextStyle}>
                {t("settlementHistory.delivery.carrier.label")}:{" "}
                {t(getDeliveryCarrierName(fulfillmentData.carrier))}
              </p>
            )}
            <p css={infoDetailTextStyle}>
              {t("settlementHistory.trackingNumber")}:{" "}
              {fulfillmentData.trackingCode}
            </p>
            <TrackingButton
              trackingCode={fulfillmentData.trackingCode}
              url={fulfillmentData.url}
            />
          </div>
        )
      )}
      {isLoadingAddress ? (
        <div css={infoWrapperStyle}>
          <h4 css={infoTitleTextStyle}>
            {t("settlementHistory.delivery.destination")}
          </h4>
          <p css={infoDetailTextStyle}>
            <Skeleton width={100} />
            <br />
            <Skeleton width={80} />
            <br />
            <Skeleton width={200} />
          </p>
        </div>
      ) : (
        shouldShowShippingInfo &&
        deliveryData && (
          <div css={infoWrapperStyle}>
            <h4 css={infoTitleTextStyle}>
              {t("settlementHistory.delivery.destination")}
            </h4>
            <p css={infoDetailTextStyle}>
              {recipientName}
              <br />〒{postalCode}
              <br />
              {mainAddress}
              {subAddress && ` ${subAddress}`}
            </p>
          </div>
        )
      )}
      {/* 一つの段落になるため、Fragmentで分離 */}
      <Fragment>
        <h4 css={[infoTitleTextStyle, hideOnDesktopView]}>
          {t("settlementHistory.orderContent")}
        </h4>
        <p css={labelWithValueStyle}>
          <span css={infoDetailTextStyle}>
            {t("settlementHistory.totalCount")}:
          </span>
          <span css={infoDetailTextStyle}>
            {purchasedCount}
            {t("settlementHistory.countUnit")}
          </span>
        </p>
        <p css={labelWithValueStyle}>
          <span css={infoDetailTextStyle}>
            {t("settlementHistory.paymentAmount")}:
          </span>
          <span css={infoDetailTextStyle}>¥{formattedPrice}</span>
        </p>
      </Fragment>
      <div css={infoWrapperStyle}>
        <p css={infoTitleTextStyle}>
          {t("settlementHistory.paymentMethodLabel")}
        </p>
        <p css={labelWithValueStyle}>
          <span css={infoDetailTextStyle}>{t(purchasedType)}:</span>
          <span
            css={
              paymentStatus === PAYMENT_STATUS.PAYSUCCESS
                ? infoDetailTextStyle
                : alertTextStyle
            }
          >
            {t(getPaymentStatusLabel(paymentStatus))}
          </span>
        </p>
        <ReceiptOrPaymentAlert
          isPayPalPending={isPayPalPending}
          isPurchased={isPurchased}
          isReceiptAvailable={isReceiptAvailable}
          onContinuePayment={onContinuePayment}
          onShowReceipt={onShowReceipt}
          showPaymentAlert={showPaymentAlert}
        />
      </div>
    </div>
  );
};
